import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import withStyles from '@mui/styles/withStyles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  addEventTemplate,
  editEventTemplate,
} from '@pingum/app-state-management/eventTemplate/actions';

import styles from './styles';

import ArchiveDeleteEventTemplateModal from '../ArchiveDeleteEventTemplateModal';
import RestoreEventTemplateModal from '../RestoreEventTemplateModal';

import { primary } from '../../../../../utils/brandColors';
import DefaultsForm from './components/DefaultsForm';
import DataForm from './components/DataForm';
import GeneralForm from './components/GeneralForm';
// import StatusesForm from './components/StatusesForm';

const resetState = (eventTemplate, barn) => {
  let defaultWorkflowStatuses = null;
  if (!eventTemplate.workflowStatuses && barn && barn.barnMetadata && barn.barnMetadata.statusStartId
    && barn.barnMetadata.statusPendingId && barn.barnMetadata.statusEndId) {
    defaultWorkflowStatuses = [
      {
        statusId: barn.barnMetadata.statusStartId,
        order: 0,
        startingStatus: true,
        requiredAssignment: false,
        nextStatusOptions: [barn.barnMetadata.statusPendingId, barn.barnMetadata.statusEndId],
      },
      {
        statusId: barn.barnMetadata.statusPendingId,
        order: 1,
        startingStatus: false,
        requiredAssignment: false,
        nextStatusOptions: [barn.barnMetadata.statusEndId],
      },
      {
        statusId: barn.barnMetadata.statusEndId,
        order: 2,
        startingStatus: false,
        requiredAssignment: false,
        nextStatusOptions: [barn.barnMetadata.statusPendingId],
      },
    ];
  }
  return {
    id: eventTemplate.id,
    showAdvanced: false,
    name: eventTemplate.id
      ? eventTemplate.name
      : '',
    description: eventTemplate.id
      ? eventTemplate.description
      : '',
    defaultStartStatus: defaultWorkflowStatuses
      ? { id: parseInt(defaultWorkflowStatuses[0].statusId, 10) }
      : eventTemplate.defaultStartStatus,
    eventData: eventTemplate.id
      ? eventTemplate.eventData
      : {},
    eventDataDefaultValues: eventTemplate.id
      ? eventTemplate.eventDataDefaultValues
      : {
        [barn.barnMetadata.calendarDisplayColorEventDataId]: primary[500],
        [barn.barnMetadata.clientViewableEventDataId]: false,
        advanceNotice: '0',
      },
    dueDateWorkingTime: eventTemplate.dueDateWorkingTime,
    tags: eventTemplate.tags || [],
    children: eventTemplate.children
      ? eventTemplate.children
      : [],
    notificationChannels: eventTemplate.notificationChannels || ['mobile', 'app'],
    configurableTags: eventTemplate.configurableTags || true,
    configurableName: eventTemplate.configurableName || true,
    configurableStartStatus: true,
    allowActiveDuplicates: eventTemplate.allowActiveDuplicates || true,
    dueDateDefaultTime: !eventTemplate.id
      ? 43200000 // default 12 hours
      : eventTemplate.dueDateDefaultTime || 0,
    dueDateDefaultTimeEndOfDay: !eventTemplate.id
      ? false
      : eventTemplate.dueDateDefaultTimeEndOfDay,
    dueDateStatusChangeId: eventTemplate.dueDateStatusChangeId || null,
    workflowStatuses: defaultWorkflowStatuses || eventTemplate.workflowStatuses,
    validation: {
      hasChanged: eventTemplate.id, // if we are copying this will be null
      name: {
        hasChanged: !!eventTemplate.name,
        isValid: true,
        message: '',
      },
      description: {
        isValid: true,
        message: '',
      },
      defaultStartStatus: { hasChanged: defaultWorkflowStatuses || (eventTemplate.defaultStartStatus && eventTemplate.defaultStartStatus.id) },
      tags: { isValid: eventTemplate.tags && eventTemplate.tags.length > 0 },
    },
    openArchiveDeleteModal: false,
    openRestoreModal: false,
    selectedEventTemplateId: null,
    snackbar: null,
  };
};

export class EventTemplateEditorComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = resetState(this.props.eventTemplate, this.props.barn);
  }

  componentDidUpdate(prevProps) {
    if (JSON.stringify(prevProps.eventTemplate) !== JSON.stringify(this.props.eventTemplate) || prevProps.modal !== this.props.modal) {
      this.setState(resetState(this.props.eventTemplate, this.props.barn));
    }
  }

  handleNameChange = (e) => {
    const changedValue = e.target.value;
    const isValid
      = changedValue !== null
      && changedValue !== undefined
      && changedValue.length >= 2
      && changedValue.length <= 50;
    this.setState((prevState) => ({
      name: changedValue,
      validation: {
        ...prevState.validation,
        name: {
          hasChanged: true,
          isValid,
          message: !isValid
            ? <span><br />Please provide a valid name (between 2 and 50 characters in length).</span>
            : '',
        },
      },
    }));
  }

  handleDescriptionChange = (e) => {
    const changedValue = e.target.value || '';
    const isValid
      = (changedValue === '' || (changedValue.length <= 1024 && changedValue.length >= 2));
    this.setState((prevState) => ({
      description: changedValue,
      validation: {
        ...prevState.validation,
        description: {
          isValid,
          message: !isValid
            ? <span><br />Please provide a valid description (between 2 and 1024 characters in length).</span>
            : '',
        },
      },
    }));
  }

  // handleDefaultStartStatusChange = (defaultStartStatus) => {
  //   this.setState((prevState) => ({
  //     defaultStartStatus,
  //     validation: {
  //       ...prevState.validation,
  //       defaultStartStatus: { hasChanged: true },
  //     },
  //   }));
  // }

  handleChangeTags = (selectedItems) => {
    this.setState((prevState) => ({
      tags: selectedItems,
      children: [], // reset children when task changes since children must be of same type
      validation: {
        ...prevState.validation,
        tags: {
          isValid: !!selectedItems
            .find((t) => t.id === this.props.barn.barnMetadata.horseTaskTypeTagId || t.id === this.props.barn.barnMetadata.barnTaskTypeTagId),
        },
      },
    }));
  }

  // handleChangeEventData = (eventData) => {
  //   this.setState({ eventData });
  // }

  handleChangeEventDataDefaultValues = (eventDataDefaultValues) => {
    this.setState({ eventDataDefaultValues });
  }

  handleChangeDueDateWorkingTime = (dueDateWorkingTime) => {
    this.setState({ dueDateWorkingTime });
  }

  handleChangeDueDateDefaultTime = (dueDateDefaultTime, dueDateDefaultTimeEndOfDay = false) => {
    this.setState({
      dueDateDefaultTime,
      dueDateDefaultTimeEndOfDay,
    });
  }

  handleChangeDateStatusChangeId = (dueDateStatusChangeId) => {
    this.setState({ dueDateStatusChangeId });
  }

  handleChangeNotificationChannels = (notificationChannels) => {
    this.setState({ notificationChannels });
  }

  // handleChangeWorkflowStatuses = (workflowStatuses) => {
  //   this.setState({ workflowStatuses });
  // }

  handleCloseArchiveDeleteModal = (backToList) => {
    this.setState({
      selectedEventTemplateId: null,
      openArchiveDeleteModal: false,
    });
    if (backToList) {
      if (this.props.onClose && this.props.modal) { this.props.onClose(); } else { this.props.navigate('/settings/tasks'); }
    }
  }

  handleCloseRestoreModal = (backToList) => {
    this.setState({
      selectedEventTemplateId: null,
      openRestoreModal: false,
    });
    if (backToList) {
      if (this.props.onClose && this.props.modal) { this.props.onClose(); } else { this.props.navigate('/settings/tasks'); }
    }
  }

  handleOpenArchiveDeleteModal = (eventTemplateId) => {
    this.setState({
      selectedEventTemplateId: eventTemplateId,
      openArchiveDeleteModal: true,
    });
  }

  handleOpenRestoreModal = (eventTemplateId) => {
    this.setState({
      selectedEventTemplateId: eventTemplateId,
      openRestoreModal: true,
    });
  }

  showActionButtons = () => this.state.validation.name.hasChanged
    && this.state.validation.defaultStartStatus.hasChanged
    && this.state.validation.name.isValid && this.state.validation.description.isValid
    && this.state.validation.tags.isValid

  submitEventTemplate = () => {
    const request = {
      name: this.state.name,
      description: this.state.description || '',
      defaultStartStatusId: this.state.defaultStartStatus.id,
      tags: this.state.tags.map((t) => t.id),
      children: this.state.children.map((et) => et.id),
      eventData: null,
      eventDataDefaultValues: this.state.eventDataDefaultValues,
      configurableTags: this.state.configurableTags,
      configurableName: this.state.configurableName,
      configurableStartStatus: true,
      allowActiveDuplicates: this.state.allowActiveDuplicates,
      dueDateDefaultTime: this.state.dueDateDefaultTime || 0,
      dueDateDefaultTimeEndOfDay: this.state.dueDateDefaultTimeEndOfDay || false,
      dueDateWorkingTime: this.state.dueDateWorkingTime,
      dueDateStatusChangeId: this.state.dueDateStatusChangeId,
      workflowStatuses: this.state.workflowStatuses,
      notificationChannels: this.state.notificationChannels,
    };

    if (this.props.eventTemplate.id) {
      this.props.editEventTemplate(this.props.eventTemplate.id, request).then((updatedEventTemplate) => {
        if (this.props.onClose && this.props.modal) { this.props.onClose(updatedEventTemplate); } else { this.props.navigate('/settings/tasks'); }
      }).catch((error) => {
        console.error(error);
        this.setState({
          snackbar: {
            children: 'Error while saving task',
            severity: 'error',
          },
        });
      });
    } else {
      this.props.addEventTemplate(request).then((newEventTemplate) => {
        if (this.props.onClose && this.props.modal) { this.props.onClose(newEventTemplate); } else { this.props.navigate('/settings/tasks'); }
      }).catch((error) => {
        console.error(error);
        this.setState({
          snackbar: {
            children: 'Error while saving task',
            severity: 'error',
          },
        });
      });
    }
  }

  render() {
    const {
      eventTemplate,
      barn,
      isLoading,
      tags,
      tagTypes,
      eventData,
      // statuses,
      classes,
      modal,
      onClose,
    } = this.props;

    const {
      openArchiveDeleteModal,
      openRestoreModal,
      selectedEventTemplateId,
      snackbar,
    } = this.state;

    return (
      <div>
        {
          !modal && (
            <Button
              onClick={() => {
                this.props.navigate(-1); // back two because we place the ?tab=0 in query param which counts as a navigation page hit
              }}
              startIcon={<ArrowBackIcon />}
            >Go Back
            </Button>
          )
        }
        <Typography variant="h5" component="div" gutterBottom>
          {
            eventTemplate.id
              ? 'Edit Task Type'
              : 'Create New Task Type'
          }
        </Typography>
        <div>
          <form className={classes.container} autoComplete="off">
            <div className={classes.formSection}>
              <GeneralForm
                eventTemplate={this.state}
                barn={barn}
                tags={this.state.tags}
                allTags={tags}
                tagTypes={tagTypes}
                eventData={eventData}
                handleNameChange={this.handleNameChange}
                handleChangeTags={this.handleChangeTags}
                handleDescriptionChange={this.handleDescriptionChange}
                isLoading={isLoading}
              />
            </div>

            <div className={classes.formSection}>
              <DataForm
                eventTemplate={this.state}
                barn={barn}
                tags={tags}
                tagTypes={tagTypes}
                eventData={eventData}
                handleChangeEventData={this.handleChangeEventData}
                handleChangeEventDataDefaultValues={this.handleChangeEventDataDefaultValues}
                isLoading={isLoading}
                isHorseTask={!!(this.state.tags || []).find((t) => t.id === barn.barnMetadata.horseTaskTypeTagId)}
                handleChangeNotificationChannels={this.handleChangeNotificationChannels}
                notificationChannels={this.state.notificationChannels}
              />
            </div>

            <div className={classes.formSection}>
              <DefaultsForm
                eventTemplate={this.state}
                statusEndId={barn.barnMetadata.statusEndId}
                handleChangeEventDataDefaultValues={this.handleChangeEventDataDefaultValues}
                handleChangeDueDateDefaultTime={this.handleChangeDueDateDefaultTime}
                handleChangeDueDateWorkingTime={this.handleChangeDueDateWorkingTime}
                handleChangeDateStatusChangeId={this.handleChangeDateStatusChangeId}
                isLoading={isLoading}
              />
            </div>

            {/* <div className={classes.formSection}>
              <StatusesForm
                eventTemplate={{
                  ...this.state,
                  workflowStatuses: this.state.workflowStatuses
                    ? this.state.workflowStatuses
                    : [],
                }}
                statuses={statuses}
                onDefaultStartStatusChange={this.handleDefaultStartStatusChange}
                onChangeWorkflowStatuses={this.handleChangeWorkflowStatuses}
                isLoading={isLoading}
              />
            </div> */}
          </form>
        </div>
        <div className={classes.cardActionsContainer}>
          <Button sx={{ mr: 1 }} onClick={() => { if (onClose && modal) { onClose(); } else { this.props.navigate('/settings/tasks'); } }}>
            Cancel
          </Button>
          {
            eventTemplate.id && (!eventTemplate.archived
              ? (
                <Button
                  className={classes.actionIconButtonDelete}
                  color="inherit"
                  sx={{ mr: 1 }}
                  onClick={() => { this.handleOpenArchiveDeleteModal(eventTemplate.id); }}
                >
                  Delete
                </Button>
              )
              : (
                <Button
                  color="secondary"
                  className={classes.actionIconButton}
                  onClick={() => { this.handleOpenRestoreModal(eventTemplate.id); }}
                  sx={{ mr: 1 }}
                >
                  Restore
                </Button>
              ))
          }
          <Button
            disabled={!this.showActionButtons()}
            id="submit-event-modal-button"
            onClick={this.submitEventTemplate}
            color="primary"
            variant="contained"
          >
            {
              eventTemplate.id
                ? 'Update'
                : 'Create'
            }
          </Button>
        </div>

        <ArchiveDeleteEventTemplateModal
          eventTemplateIds={[selectedEventTemplateId]}
          handleCloseModal={this.handleCloseArchiveDeleteModal}
          isOpen={openArchiveDeleteModal}
        />
        <RestoreEventTemplateModal
          eventTemplateIds={[selectedEventTemplateId]}
          handleCloseModal={this.handleCloseRestoreModal}
          isOpen={openRestoreModal}
        />

        {!!snackbar && (
          <Snackbar
            open
            onClose={() => { this.setState({ snackbar: null }); }}
            autoHideDuration={6000}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
          >
            <Alert {...snackbar} onClose={() => { this.setState({ snackbar: null }); }} />
          </Snackbar>
        )}
      </div>
    );
  }
}

EventTemplateEditorComponent.propTypes = {
  classes: PropTypes.object,
  eventTemplate: PropTypes.object,
  barn: PropTypes.object,
  addEventTemplate: PropTypes.func,
  editEventTemplate: PropTypes.func,
  statuses: PropTypes.array,
  eventData: PropTypes.array,
  tags: PropTypes.array,
  tagTypes: PropTypes.array,
  navigate: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isEditing: PropTypes.bool,
  isAdding: PropTypes.bool,
  errors: PropTypes.number,
  modal: PropTypes.bool,
  onClose: PropTypes.func,
};

EventTemplateEditorComponent.defaultProps = {
  classes: {},
  barn: { barnMetadata: {} },
  eventTemplate: {
    name: '',
    description: '',
    eventData: [],
    tags: [],
    allowActiveDuplicates: true,
    eventDataDefaultValues: {},
    workflowStatuses: [],
    configurableTags: true,
    configurableName: true,
    configurableStartStatus: true,
    defaultStartStatus: {
      id: 0,
      name: 'Select A Value...',
      icon: 'search',
      iconColor: 'gray',
    },
  },
  addEventTemplate: () => { },
  editEventTemplate: () => { },
  statuses: [],
  eventData: [],
  tags: [],
  tagTypes: [],
  isLoading: true,
  isEditing: false,
  isAdding: false,
  errors: 0,
  modal: false,
  onClose: null,
};

export const mapStateToProps = (state) => ({
  barn: state.barn.selectedBarn,
  statuses: state.status.statuses,
  eventData: state.eventData.eventData,
  tags: state.tag.tags,
  tagTypes: state.tagType.tagTypes,
  isEditing: state.eventTemplate.isEditingEventTemplate,
  isAdding: state.eventTemplate.isAddingEventTemplate,
  errors: state.error.errorMessages.length,
});

export const mapDispatchToProps = (dispatch) => ({
  addEventTemplate: (request) => dispatch(addEventTemplate(request)),
  editEventTemplate: (eventTemplateId, request) => dispatch(editEventTemplate(eventTemplateId, request)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(EventTemplateEditorComponent));
